
import React, { MutableRefObject, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts } from "../../../Styles";
import { DataObj, ListData_ValueTypes, QuickEntry, quickEntryChange, SelectFieldOption_Values, TableStyles_Types } from "../../../Definitions";
import { UndefinedOptions, useFeedcurveOptions, useGroupOptions, useMilestoneOptions, useSectionOptions } from "../../_Components";

// Table
import { GenericDataParameters, GenericRow, useColumnConstructor } from "../../Tables/_Tables/useColumnConstructor";
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, noop, Row, useReactTable } from "@tanstack/react-table";
import { ReactiveTable } from "../../Tables/reactiveTableRenderer";

// Data
import { useAppSelector } from "../../../Store";
import { Graph_QL, useFeedCurves, MaintainAnimal } from "../../../Services";
import { FilteredAnimalsResponse, GET_FILTERED_ANIMALS } from "../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/getFilteredAnimalsCall";
import { useQuery } from "@apollo/client";
import { keyBy, merge } from "lodash";


type QuickEntryModalLvl3Props = {
  quickEntryData: QuickEntry[];
  quickEntryChange: quickEntryChange[];
  dirtyDataRef: MutableRefObject<MaintainAnimal[]>
  focus_view?: boolean
};
interface TableRow extends GenericRow {
  index: number;
  animalNo: number;
  animalGroup?: { label: string; id: number };
  animalLatestMilestone?: { label: string; id: number };
  animalFeedcurve?: SelectFieldOption_Values;
  animalLocation?: { label: string; id: number };
  animalMilestoneDay?: number;
};

const camelToSnakeCase = (stringValue: string) => {
  // the table id for feed curve is with misspelled feedcurve in the database
  return stringValue === "feedCurveId" ? "feedcurve_id": stringValue.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
};

export default function QuickEntryModalLvl3({ quickEntryData, quickEntryChange, dirtyDataRef, focus_view }: QuickEntryModalLvl3Props) {
  const { t } = useTranslation();
  const [editTable] = useState(true)
  
  const groupOptions = useGroupOptions(UndefinedOptions.nan);
  const { feedCurveOptions } = useFeedcurveOptions(UndefinedOptions.nan);
  const milestoneOptions = useMilestoneOptions();
  const {locationOptions} = useSectionOptions(UndefinedOptions.nan)

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);
  const { data: feedCurvesState } = useFeedCurves(connection); 

  const feedCurves = useMemo(() => {
    if (feedCurvesState && feedCurvesState[domain]) {
      return feedCurvesState[domain].data;
    }
    return [];
  }, [domain, feedCurvesState]);
  const filters = quickEntryData.map((filter) => ({
    ids: filter.value.map(({ id }) => id),
    key: camelToSnakeCase(filter.key),
  }));

  const { loading, data } = useQuery<FilteredAnimalsResponse>(
    GET_FILTERED_ANIMALS,
    { variables: { domain: farm, filters } }
  );
  const tableData = useMemo(() => {
    if (!data?.animal.filtered) return [];
  
    return data.animal.filtered
    .map((filteredAnimal, index): TableRow | null => {

      const animalGroupChange = quickEntryChange.find(q => q.key === "animal_group_id");
      const milestoneChange = quickEntryChange.find(q => q.key === "milestone_id");
      const locationChange = quickEntryChange.find(q => q.key === "location_id");
      const feedCurveChange = quickEntryChange.find(q => q.key === "feedCurveId");
      const milestoneDayChange = quickEntryChange.find(q => q.key === "milestone_day" as unknown as DataObj);
      const animalFeedCurve = feedCurves.find(({feedCurveId}) => (
        feedCurveId === (feedCurveChange?.value[0].id ?? filteredAnimal.feedcurve_id)
      ));
      const animalMilestone = animalFeedCurve?.milestones?.find(({milestoneId}) => (milestoneId === milestoneChange?.value[0].id)) ?? animalFeedCurve?.milestones?.find(({milestoneId}) => (milestoneId === filteredAnimal.milestone_id));
        
      const changedData: MaintainAnimal = { animal_id: filteredAnimal.animal_id };
      
      if (animalGroupChange) {
        changedData.animal_group_id = animalGroupChange.value[0].id          
      }
      if (milestoneChange && animalMilestone && (animalMilestone.milestoneId !== filteredAnimal.milestone_id || filteredAnimal.feedcurve_id !== animalFeedCurve?.feedCurveId)) {
        changedData.fk_feedcurve_milestones = animalMilestone.feedCurveMilestoneId;
      }
      if (feedCurveChange) {
        changedData.feedcurve_id = feedCurveChange.value[0].id
      }
      if (locationChange) {
        changedData.location = locationChange.value[0].id
      }
      if (milestoneDayChange) {
        const value = milestoneDayChange.value[0].id
        changedData.milestone_day = Math.round((Date.now() - Number(value) * 86400000) / 1000);
      }
      dirtyDataRef.current = Object.values(merge({}, keyBy(dirtyDataRef.current, "animal_id"), keyBy([changedData], "animal_id")
      ));
      
      const _groupOptions = groupOptions.filter(option => option.id !== undefined) as { label: string; id: number }[]
      const _locationOptions = locationOptions.filter(option => option.id !== undefined) as { label: string; id: number }[]

      return {
        id: filteredAnimal.animal_id,
        animalNo: filteredAnimal.animal_number,
        animalGroup: _groupOptions.find(({ id }) => id === animalGroupChange?.value[0].id || ((Number.isNaN(id) && Number.isNaN(animalGroupChange?.value[0].id)))),
        animalLatestMilestone: animalMilestone ? {id: animalMilestone.milestoneId, label: animalMilestone.name}: undefined, 
        animalFeedcurve: animalFeedCurve ? { id: animalFeedCurve.feedCurveId, label: animalFeedCurve.name } : {id: NaN, label: "no feed curve"},
        animalLocation: _locationOptions.find(({ id }) => id === locationChange?.value[0].id || ((Number.isNaN(id) && Number.isNaN(locationChange?.value[0].id)))),
        animalMilestoneDay: milestoneDayChange?.value[0].id,
        index,
      };
    })
    .filter((row): row is TableRow => row !== null);
  }, [data?.animal.filtered, quickEntryChange, feedCurves, dirtyDataRef, groupOptions, locationOptions]);
  
  const accessorData = useMemo<GenericDataParameters<TableRow>[]>(() => {
    const rows: GenericDataParameters<TableRow>[] = [
      { headerValue: `${t("Animal No.", { defaultValue: "Animal No." })}`, accessor: "animalNo", type: ListData_ValueTypes.String, focus_view: focus_view }
    ];
    if (quickEntryChange.some(f => f.key === "milestone_day" as unknown as DataObj)) {
      rows.push({ headerValue: t("params.milestoneDay", { defaultValue: "milestone Day" }), accessor: "animalMilestoneDay", type: ListData_ValueTypes.Number, focus_view: focus_view });
    }  
    if (quickEntryChange.some(f => f.key === "animal_group_id")) {
      rows.push({ headerValue: t("params.group", { defaultValue: "group" }), accessor: "animalGroup", type: ListData_ValueTypes.List, options: groupOptions, focus_view: focus_view });
    }
    if (quickEntryChange.some(f => f.key === "location_id")) {
      rows.push({ headerValue: t("params.location", { defaultValue: "location" }), accessor: "animalLocation", type: ListData_ValueTypes.List, options: locationOptions, focus_view: focus_view });
    }
    if (quickEntryChange.some(f => f.key === "milestone_id")) {
      rows.push({ headerValue: t("params.milestone", { defaultValue: "milestone" }), accessor: "animalLatestMilestone", focus_view: focus_view, type: ListData_ValueTypes.List, options: (row?: Row<TableRow>) => {
          if (!row) {
            return milestoneOptions;
          }
          return (
            feedCurves
              .find(
                (feedCurve) =>
                  row.original.animalFeedcurve?.id === feedCurve.feedCurveId
              )
              ?.milestones?.map((milestone) => ({
                id: milestone.feedCurveMilestoneId,
                label: milestone.name,
              })) ?? []
          );
        }, });
    }

    if (quickEntryChange.some(f => f.key === "feedCurveId")) {
      rows.push({ headerValue: t("params.feedCurve", { defaultValue: "feed curve" }), accessor: "animalFeedcurve", type: ListData_ValueTypes.List, options: feedCurveOptions, focus_view: focus_view });
    }
  
    return rows;
  }, [t, focus_view, quickEntryChange, groupOptions, locationOptions, feedCurves, milestoneOptions, feedCurveOptions]);

  const columns = useColumnConstructor({
    accessorData: [...accessorData], 
    tableStyle: focus_view ? TableStyles_Types.primaryTable : TableStyles_Types.blueTable, 
    canEdit: editTable 
  });

  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      columnPinning: {
        left: ["animalNo"]
      }
    },
    meta: {
      deleteRow: noop,
      updateData: (rowId, columnId, value, row) => {
        if (columnId === "animalGroup") {
          const selectValue = value as any as SelectFieldOption_Values;
          dirtyDataRef.current = Object.values(
            merge({}, keyBy(dirtyDataRef.current, "animal_id"), keyBy([{
              animal_id: rowId,
              animal_group_id: selectValue.id
            }], "animal_id")
            )
          );
          return;
        }
        if (columnId === "animalLocation") {
          const selectValue = value as any as SelectFieldOption_Values;
          dirtyDataRef.current = Object.values(
            merge({}, keyBy(dirtyDataRef.current, "animal_id"), keyBy([{
              animal_id: rowId,
              location_id: selectValue.id
            }], "animal_id")
            )
          );
          return;
        }
        if (columnId === "animalMilestoneDay") {
          const newValue = Date.now() - Number(value) * 86400000;
          dirtyDataRef.current = Object.values(
            merge({}, keyBy(dirtyDataRef.current, "animal_id"), keyBy([{
              animal_id: rowId,
              milestone_day: Math.round(newValue / 1000)
            }], "animal_id")
            )
          );
          return;
        }
        if (columnId === "animalFeedcurve") {
          const selectValue = value as any as SelectFieldOption_Values;

          const selectedFeedCurve = feedCurves.find(
            (feedCurve) => feedCurve.feedCurveId === Number(selectValue.id)
          );

          dirtyDataRef.current = Object.values(
            merge({}, keyBy(dirtyDataRef.current, "animal_id"), keyBy([{
              animal_id: rowId,
              feedcurve_id: selectValue.id,
              fk_feedcurve_milestones: selectedFeedCurve?.milestones
                ?.length
                ? selectedFeedCurve?.milestones[0].feedCurveMilestoneId
                : undefined,
            }],"animal_id"))
          );
          return;
        }
        if (columnId === "animalLatestMilestone") {
          const selectValue = value as any as SelectFieldOption_Values;
          if (row.original.animalFeedcurve?.id) {
            const animalFeedCurve = feedCurves.find(
              (feedCurve) =>
                feedCurve.feedCurveId === row.original.animalFeedcurve?.id
            );
            const milestone = animalFeedCurve?.milestones?.find(
              (milestone) =>
                milestone.feedCurveMilestoneId === Number(selectValue.id)
            );
            if (milestone) {
              dirtyDataRef.current = Object.values(
                merge({}, keyBy(dirtyDataRef.current, "animal_id"), keyBy([{
                  animal_id: rowId,
                  fk_feedcurve_milestones: milestone.feedCurveMilestoneId
                }], "animal_id" ))
              );
            }
          }
          return;
        }
      }
    }
  })

  const reactiveTable = useMemo(() => (
    <ReactiveTable
      table={table}
      tableType={focus_view ? TableStyles_Types.primaryTable : TableStyles_Types.blueTable}
      emptyText={t("noAnimalsMatchesTheSelect", { defaultValue: "No animals matches the select" })}
      renderFooter={false}
    />
  ), [focus_view, t, table]);
  
  if (loading) {
    return ( <div style={{width: "100%", display: "flex", justifyContent: "center"}}>
      <p style={{...fonts.paragraph, ...(focus_view ? fonts.fontSize3 : fonts.fontSize4), color: colors.greyLightTertiary}}>Loading…</p>
    </div> );
  }
  
  return (
    <div style={{ height: "100%", position: "relative", backgroundColor: focus_view ? colors.greyLightSecondary : colors.blueDarkPrimary, borderRadius: focus_view ? "10px" : "0px", border: focus_view ? "5px solid "+colors.greyPrimary : "none", boxSizing: "border-box", overflow: "hidden" }}>
      {tableData.length === 0 ? <p style={{...fonts.fontSize4, ...fonts.paragraph, color: colors.greyLightTertiary, textAlign: "center"}}>{t("animalPages.animals.noAnimalsMatchTheSelection", { defaultValue: "No animals match the selection" })}</p> : reactiveTable}
      { focus_view ? <></> : <div style={{ width: "12px", height: "24px", backgroundColor: colors.blueDarkPrimary, position: "absolute", top: "0px", right: "0px" }} />}
    </div>
  );
}